<template>
    <div
        :data-color-slug="color.slug"
        :tabindex="disabled ? -1 : 0"
        :aria-label="color.label"
        :aria-expanded="String(isHovered)"
        aria-haspopup="true"
        class="group relative inline-flex cursor-pointer focus:outline-none"
        @focusin="handleFocusIn($event)"
        @focusout="handleFocusOut($event)"
        @keydown.esc.stop="handleEscape($event)"
    >
        <button
            type="button"
            data-role="color-swatch"
            class="block h-5 w-5 cursor-pointer rounded-md border-none transition-opacity duration-150 tap-target hover:opacity-80 group-focus-visible:ring-2 group-focus-visible:ring-theme-accent group-focus-visible:ring-offset-2"
            :class="activeClasses"
            :style="{ backgroundColor: color.color }"
            :aria-pressed="String(selectedSlug === color.slug)"
            :disabled="disabled"
            @click.stop="$emit('select', { slug: color.slug, color: color.color })"
            @mouseenter="handleSwatchHover($event)"
            @mouseleave="scheduleClose"
        ></button>

        <!-- Hover/focus popover: label + actions -->
        <div
            v-if="isHovered"
            role="group"
            :aria-label="color.label"
            class="absolute bottom-[calc(100%+4px)] z-10 flex items-center gap-1.5 whitespace-nowrap rounded-md border border-theme-border bg-theme-bg px-2 py-1 shadow-md"
            :class="popoverPositionClasses"
            @mouseenter="cancelClose"
            @mouseleave="scheduleClose"
        >
            <span class="text-[11px] font-medium leading-none text-theme-text">
                {{ color.label }}
            </span>
            <div class="flex gap-0.5">
                <!-- Edit button -->
                <button
                    type="button"
                    class="flex h-5 w-5 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 text-theme-toolbar-icon transition-colors duration-150 hover:bg-theme-icon-bg hover:text-theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                    :title="localizations[lang].edit"
                    :aria-label="localizations[lang].edit + ': ' + color.label"
                    @click.stop="emitEditRequest"
                >
                    <SvgIcon icon="edit" width="11" height="11" />
                </button>
                <!-- Remove button -->
                <button
                    type="button"
                    class="flex h-5 w-5 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0 text-theme-toolbar-icon transition-colors duration-150 hover:bg-theme-danger-bg hover:text-theme-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent disabled:cursor-not-allowed disabled:opacity-25"
                    :title="isInUse ? localizations[lang].highlighterInUse : localizations[lang].remove"
                    :aria-label="
                        (isInUse ? localizations[lang].highlighterInUse : localizations[lang].remove) +
                        ': ' +
                        color.label
                    "
                    :disabled="isInUse"
                    @click.stop="handleRemove"
                >
                    <SvgIcon icon="trash" width="11" height="11" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

export default {
    name: 'HighlighterCustomColor',

    props: {
        /**
         * The custom color object with slug, color, and label.
         */
        color: {
            type: Object,
            required: true,
        },
        /**
         * Currently selected color slug for active indicator.
         */
        selectedSlug: {
            type: String,
            default: '',
        },
        /**
         * Layout variant for active indicator style.
         */
        variant: {
            type: String,
            default: 'standard',
            validator: value => ['compact', 'standard'].includes(value),
        },
        /**
         * Removes the swatch from the tab order and blocks selection.
         */
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['select', 'edit'],

    data() {
        return {
            isHovered: false,
            popoverAlignment: 'center',
            suppressFocusOpen: false,
        };
    },

    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['usedHighlightColorSlugs']),

        isInUse() {
            return this.usedHighlightColorSlugs.includes(this.color.slug);
        },

        activeClasses() {
            const isActive = this.selectedSlug === this.color.slug;

            if (this.variant === 'compact') {
                return {
                    'relative transition-transform duration-50 ease-in': isActive,
                    "after:content-[''] after:absolute after:inset-0 after:border after:border-solid after:border-2 after:border-current after:rounded-md after:scale-120":
                        isActive,
                };
            }

            return { 'ring-2 ring-theme-text ring-offset-2': isActive };
        },

        popoverPositionClasses() {
            switch (this.popoverAlignment) {
                case 'left':
                    return 'left-0';
                case 'right':
                    return 'right-0';
                default:
                    return 'left-1/2 -translate-x-1/2';
            }
        },
    },

    created() {
        this.busEventName = `confirmRemoveCustomColor-${this._uid}`;
        this.$bus.$on(this.busEventName, this.executeRemove);
    },

    beforeDestroy() {
        this.$bus.$off(this.busEventName, this.executeRemove);
        this.cancelClose();
    },

    methods: {
        ...mapActions(['removeAndSaveHighlighter']),
        ...mapMutations(['openConfirmationModal', 'openToast']),

        // ── Popover ─────────────────────────────────────────────

        openPopover(target) {
            this.cancelClose();
            this.isHovered = true;
            this.popoverAlignment = this.calculateAlignment(target);
        },

        closePopover() {
            this.cancelClose();
            this.isHovered = false;
            this.popoverAlignment = 'center';
        },

        /**
         * Opens popover when the swatch button is hovered.
         * @param {MouseEvent} event
         */
        handleSwatchHover(event) {
            this.openPopover(event.currentTarget.parentElement);
        },

        /**
         * Schedules popover close after a short delay.
         * Allows cursor to move from swatch to popover without closing.
         */
        scheduleClose() {
            this.closeTimer = setTimeout(() => {
                this.closePopover();
            }, 100);
        },

        /**
         * Cancels a pending close when cursor enters popover.
         */
        cancelClose() {
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },

        handleFocusIn(event) {
            if (this.suppressFocusOpen) return;
            if (this.isHovered) return;
            this.openPopover(event.currentTarget);
        },

        handleFocusOut(event) {
            if (!this.isHovered) return;
            const next = event.relatedTarget;
            if (next && event.currentTarget.contains(next)) return;
            this.closePopover();
        },

        handleEscape(event) {
            if (!this.isHovered) return;
            const wrapper = event.currentTarget;
            this.closePopover();
            this.suppressFocusOpen = true;
            this.$nextTick(() => {
                wrapper?.focus();
                this.$nextTick(() => {
                    this.suppressFocusOpen = false;
                });
            });
        },

        /**
         * Calculates popover horizontal alignment based on element position.
         * @param {HTMLElement} element - The swatch wrapper element
         * @returns {'left' | 'center' | 'right'}
         */
        calculateAlignment(element) {
            const parent = this.$el?.parentElement;
            if (!parent || !element) return 'center';

            const parentRect = parent.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const elementCenterX = elementRect.left + elementRect.width / 2;
            const relativePosition = (elementCenterX - parentRect.left) / parentRect.width;

            if (relativePosition < 0.25) return 'left';
            if (relativePosition > 0.75) return 'right';
            return 'center';
        },

        // ── Edit ────────────────────────────────────────────────

        /**
         * Asks the picker, which owns the palette row, to open the edit form on this color.
         */
        emitEditRequest() {
            this.closePopover();
            this.$emit('edit', this.color);
        },

        // ── Remove ──────────────────────────────────────────────

        /**
         * Opens confirmation modal to remove this custom highlighter.
         */
        handleRemove() {
            if (this.isInUse) return;

            this.closePopover();

            const l = this.localizations[this.lang];

            this.openConfirmationModal({
                title: l.confirmDeleteHighlighter.title,
                message: l.confirmDeleteHighlighter.message.replace('{label}', this.color.label),
                confirmLabel: l.delete,
                cancelLabel: l.cancel,
                onConfirmAction: this.busEventName,
                context: { slug: this.color.slug },
            });
        },

        /**
         * Executes removal after confirmation. Awaits the persistence so the
         * color stays in the palette when the server rejects the deletion
         * (e.g. notes still attached to its slug); on rejection, surfaces the
         * server-provided reason via a toast.
         * @param {{ slug: string }} context - Context from confirmation modal
         */
        async executeRemove(context) {
            if (context?.slug !== this.color.slug) return;
            if (this.isInUse) return;

            try {
                await this.removeAndSaveHighlighter(this.color.slug);
            } catch (error) {
                const fallback = this.localizations[this.lang].highlighterDeletionFailed;
                this.openToast({ text: error?.message || fallback });
            }
        },
    },
};
</script>
